import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from './config';

// Slowniki maja informacje zakodowana w doc.id w formacie "{SHORT} - {FULL}".
// Cala apka CPT (z ktorej pochodza dane) trzyma to tak. Pomocniczy parser.
export function parseDictEntry(fullString) {
  const parts = (fullString || '').split(' - ');
  return {
    shortForm: (parts[0] || '').trim(),
    fullName: parts.slice(1).join(' - ').trim(),
  };
}

// --- Model LEGACY: osobna kolekcja top-level per slownik, doc.id = "{SHORT} - {FULL}".
// Uzywany m.in. przez projekt gcckqgiyfpjztexwpi.
async function fetchLegacyDictionary(collectionName) {
  try {
    const snap = await getDocs(collection(db, collectionName));
    if (snap.empty) return [];
    return snap.docs.map(d => {
      const { shortForm, fullName } = parseDictEntry(d.id);
      return { fullString: d.id, shortForm, fullName };
    });
  } catch (err) {
    console.error(`Blad pobierania slownika legacy ${collectionName}:`, err);
    return [];
  }
}

// --- Model NOWY (portal GiODE, np. projekt pfeptyumpcdmfqf): jedna kolekcja `dictionaries`,
// gdzie kazdy dokument to caly slownik: { displayName, category, groupId, entries: [...] }.
// Pojedynczy wpis: { name: <skrot>, description: <pelna nazwa>, index, isActive, ... }.
// Grupa `pn_b_02480` — norma PN-B-02480, ta ktorej odpowiadaja kody uzywane w przekrojach
// (G, Gp, KR, KRg, SM, ST...). Druga grupa (PN-EN ISO 14688-2) ma inna nomenklature.
const DICT_GROUP_ID = 'pn_b_02480';

let dictionariesCachePromise = null;

async function fetchAllDictionaryDocs() {
  if (!dictionariesCachePromise) {
    dictionariesCachePromise = (async () => {
      const q = query(collection(db, 'dictionaries'), where('groupId', '==', DICT_GROUP_ID));
      const snap = await getDocs(q);
      return snap.docs.map(d => ({ id: d.id, ...d.data() }));
    })().catch(err => {
      console.error('Blad pobierania kolekcji dictionaries:', err);
      dictionariesCachePromise = null; // pozwol na retry przy nastepnym wywolaniu
      return [];
    });
  }
  return dictionariesCachePromise;
}

async function fetchGrouped(displayName) {
  const docs = await fetchAllDictionaryDocs();
  const dict = docs.find(d => d.displayName === displayName);
  if (!dict || !Array.isArray(dict.entries)) return [];
  return dict.entries
    .filter(e => e && e.isActive !== false && (e.name || '').trim())
    .map(e => {
      const shortForm = (e.name || '').trim();
      const fullName = (e.description || '').trim();
      return {
        // fullString musi zostac w formacie "{SHORT} - {FULL}" — jest uzywany jako
        // value w <select> i przepuszczany przez parseDictEntry w innych miejscach.
        fullString: fullName ? `${shortForm} - ${fullName}` : shortForm,
        shortForm,
        fullName,
      };
    });
}

// Najpierw nowy model (`dictionaries`), fallback do legacy — dzieki temu ten sam kod
// dziala na obu projektach bez przelaczania configu.
function makeDictionaryFetcher(displayName, legacyCollection) {
  return async () => {
    let entries = await fetchGrouped(displayName);
    if (entries.length === 0) entries = await fetchLegacyDictionary(legacyCollection);
    return entries.sort((a, b) => a.shortForm.localeCompare(b.shortForm, 'pl'));
  };
}

export const fetchLithologyDictionary =
  makeDictionaryFetcher('Gr. Mineralne podkategoria', 'catSoilLithology');
export const fetchGenesisStratigraphyDictionary =
  makeDictionaryFetcher('Geneza i Stratygrafia', 'catGenesisAndStratiography');
export const fetchMineralRockDictionary =
  makeDictionaryFetcher('Grunty Mineralne i Skaliste', 'catMineralRockSoils');
export const fetchRockSubcategoryDictionary =
  makeDictionaryFetcher('Gr. Skaliste podkategoria', 'subCatRockySoil');

// Rozparsowuje display-name warstwy (np. "SM(Łi)", "πp + K", "SM(Łi//Pc)", "KRg(okr.Łi+I)")
// na sloty litologii + przewarstwienia. Każdy slot dopasowywany do słownika po shortForm
// — żeby <select value={fullString}> rozpoznał opcję.
// Zwraca obiekt z polami lithologyRockSoil01/02/03 + lithologyRockSoilFullName01/02/03 + layeringRockSoil01/02.
export function parseLithologyName(name, { lithoDict = [], rockDict = [], rockSubDict = [], isRock = false } = {}) {
  if (!name) return {};
  const result = {};

  // Szukaj we wszystkich słownikach — najpierw wg trybu (skala/grunt), potem fallback do drugiej puli.
  const primary = isRock ? [...rockDict, ...rockSubDict] : lithoDict;
  const secondary = isRock ? lithoDict : [...rockDict, ...rockSubDict];
  const findByShort = (s) => primary.find(e => e.shortForm === s) || secondary.find(e => e.shortForm === s);

  // Rozbij string po separatorach " + " | " // " | " / " | "+" | "//" | "/",
  // IGNORUJĄC zawartość nawiasów.
  // Dzięki temu "KWg(I//okr.Łi)" pozostaje jednym slotem, a "KWg(Pc) + ST(Pd)" rozbija się na 2.
  const splitTopLevel = (s) => {
    if (!s) return { parts: [], seps: [] };
    const parts = [];
    const seps = [];
    let depth = 0;
    let cur = '';
    let i = 0;
    while (i < s.length) {
      const c = s[i];
      if (c === '(') { depth++; cur += c; i++; continue; }
      if (c === ')') { depth--; cur += c; i++; continue; }
      if (depth === 0) {
        const rest = s.slice(i);
        // "//" MUSI byc sprawdzone przed "/" — inaczej podwojny ukosnik rozpadlby sie
        // na dwa pojedyncze. Pojedyncze "/" wystepuje w danych (16× w DK9 II).
        const sepMatch = rest.match(/^\s*(\/\/|\/|\+)\s*/);
        if (sepMatch) {
          parts.push(cur.trim());
          seps.push(sepMatch[1]);
          cur = '';
          i += sepMatch[0].length;
          continue;
        }
      }
      cur += c;
      i++;
    }
    if (cur.trim()) parts.push(cur.trim());
    return { parts: parts.filter(p => p.length), seps };
  };

  if (isRock) {
    // Skała: każdy slot to pełny composite "RockShort(SubShort1 sep SubShort2 ...)"
    // Zostawiamy nawiasy nietknięte — LithologyForm rozparsuje je przez parseSlotFull.
    const { parts, seps } = splitTopLevel(name);
    ['01', '02', '03'].forEach((suffix, i) => {
      const part = parts[i];
      if (!part) return;
      result[`lithologyRockSoil${suffix}`] = part;
      // Self-heal w LithologyForm dorobi lithologyRockSoilFullName przez lookup po short.
      const rockMatch = part.match(/^([^(]+)/);
      const rockShort = rockMatch ? rockMatch[1].trim() : part;
      const entry = findByShort(rockShort);
      if (entry) {
        const sub = part.match(/\(([^)]+)\)/)?.[1] || '';
        result[`lithologyRockSoilFullName${suffix}`] = sub ? `${entry.fullName} (${sub})` : entry.fullName;
      }
    });
    if (seps[0]) result.layeringRockSoil01 = seps[0];
    if (seps[1]) result.layeringRockSoil02 = seps[1];
    if (parts[0]) {
      const rockMatch = parts[0].match(/^([^(]+)/);
      result.lithologyRock01 = rockMatch ? rockMatch[1].trim() : parts[0];
    }
    return result;
  }

  // Grunt: rozbij nawias na osobne sloty (np. "nN(tłuczeń)" → slot1=nN, slot2=tłuczeń)
  const m = name.match(/^([^()]+?)\s*(?:\(([^()]*)\))?\s*$/);
  if (!m) return {};
  const main = (m[1] || '').trim();
  const inside = (m[2] || '').trim();

  let allParts, allSeps;
  if (inside) {
    const inner = splitTopLevel(inside);
    allParts = [main, ...inner.parts];
    allSeps = ['', ...inner.seps];
  } else {
    const r = splitTopLevel(main);
    allParts = r.parts;
    allSeps = r.seps;
  }

  ['01', '02', '03'].forEach((suffix, i) => {
    const part = allParts[i];
    if (!part) return;
    const entry = findByShort(part);
    result[`lithologyRockSoil${suffix}`] = entry ? entry.shortForm : part;
    if (entry) result[`lithologyRockSoilFullName${suffix}`] = entry.fullName;
  });
  if (allSeps[0]) result.layeringRockSoil01 = allSeps[0];
  if (allSeps[1]) result.layeringRockSoil02 = allSeps[1];

  return result;
}

// Sklada display-name warstwy z 3 slotow litologii + 2 przewarstwien.
// Przewarstwienie pojawia sie wlasciwie tylko gdy obie strony maja wartosc.
// Sloty bez przewarstwienia laczone spacja (np. "Pd Ps" zamiast "Pd + Ps").
export function buildCompositeLithologyName(layer) {
  const s1 = layer.lithologyRockSoil01 || '';
  const s2 = layer.lithologyRockSoil02 || '';
  const s3 = layer.lithologyRockSoil03 || '';
  const sep1 = layer.layeringRockSoil01 || '';
  const sep2 = layer.layeringRockSoil02 || '';
  let out = s1;
  if (s2) out += (sep1 ? ` ${sep1} ` : ' ') + s2;
  if (s3) out += (sep2 ? ` ${sep2} ` : ' ') + s3;
  return out.trim();
}
